import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [theme, setTheme] = useState('abacus');

  useEffect(() => {
    // Load theme preferences from localStorage
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    const savedTheme = localStorage.getItem('theme') || 'abacus';
    
    setIsDarkMode(savedDarkMode);
    setTheme(savedTheme);
    
    // Apply theme to document
    applyTheme(savedTheme, savedDarkMode);
  }, []);

  const applyTheme = (themeName, darkMode) => {
    const root = document.documentElement;
    
    // Remove existing theme classes
    root.classList.remove('theme-abacus', 'theme-classic', 'theme-modern');
    root.classList.remove('dark-mode', 'light-mode');
    
    // Add new theme classes
    root.classList.add(`theme-${themeName}`);
    root.classList.add(darkMode ? 'dark-mode' : 'light-mode');
  };

  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    applyTheme(theme, newDarkMode);
  };

  const changeTheme = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    applyTheme(newTheme, isDarkMode);
  };

  const getThemeColors = () => {
    const themes = {
      abacus: {
        light: {
          primary: '#2E7D32',      // Deep green (accounting)
          secondary: '#1976D2',    // Professional blue
          accent: '#FF6F00',       // Orange accent
          background: '#F8F9FA',   // Light gray
          surface: '#FFFFFF',      // White
          text: '#212529',         // Dark text
          textSecondary: '#6C757D', // Gray text
          border: '#DEE2E6',       // Light border
          success: '#28A745',      // Green
          warning: '#FFC107',      // Yellow
          danger: '#DC3545',       // Red
          info: '#17A2B8'          // Blue
        },
        dark: {
          primary: '#4CAF50',      // Brighter green
          secondary: '#2196F3',    // Brighter blue
          accent: '#FF9800',       // Brighter orange
          background: '#121212',   // Dark background
          surface: '#1E1E1E',      // Dark surface
          text: '#FFFFFF',         // White text
          textSecondary: '#B0B0B0', // Light gray text
          border: '#333333',       // Dark border
          success: '#4CAF50',      // Green
          warning: '#FFC107',      // Yellow
          danger: '#F44336',       // Red
          info: '#00BCD4'          // Cyan
        }
      },
      classic: {
        light: {
          primary: '#1B365D',      // Navy blue
          secondary: '#C8102E',    // Red
          accent: '#FFD700',       // Gold
          background: '#F5F5F5',
          surface: '#FFFFFF',
          text: '#000000',
          textSecondary: '#666666',
          border: '#CCCCCC',
          success: '#28A745',
          warning: '#FFC107',
          danger: '#DC3545',
          info: '#17A2B8'
        },
        dark: {
          primary: '#4A90E2',
          secondary: '#E74C3C',
          accent: '#F39C12',
          background: '#0D1117',
          surface: '#161B22',
          text: '#F0F6FC',
          textSecondary: '#8B949E',
          border: '#30363D',
          success: '#28A745',
          warning: '#FFC107',
          danger: '#DC3545',
          info: '#17A2B8'
        }
      }
    };

    return themes[theme]?.[isDarkMode ? 'dark' : 'light'] || themes.abacus.light;
  };

  const value = {
    isDarkMode,
    theme,
    toggleDarkMode,
    changeTheme,
    getThemeColors,
    themes: ['abacus', 'classic']
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
